import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, getStoredToken, setStoredToken } from "../lib/api";
import { identify, resetIdentity, track } from "../lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = logged out, object = logged in
  const [user, setUser] = useState(null);

  const checkSession = useCallback(async () => {
    // If we have no stored token AND no cookie context, /auth/me will 401
    // and we'll show the logged-out state immediately. With a stored token
    // we attempt a silent refresh.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      identify(data);
    } catch {
      setStoredToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", {
        email: (email || "").trim().toLowerCase(),
        password,
      });
      // 2FA gate — admin needs to confirm with a TOTP code before we mint a session.
      if (data?.requires_2fa && data?.totp_token) {
        return {
          ok: true,
          requires_2fa: true,
          totp_token: data.totp_token,
          ttl_s: data.ttl_s || 300,
        };
      }
      if (data?.access_token) setStoredToken(data.access_token);
      setUser(data.user);
      identify(data.user);
      track.loginSuccess();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const loginVerify2FA = async (totpToken, code) => {
    try {
      const { data } = await api.post("/auth/login-2fa", {
        totp_token: totpToken,
        code: (code || "").trim(),
      });
      if (data?.access_token) setStoredToken(data.access_token);
      setUser(data.user);
      identify(data.user);
      track.loginSuccess();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password, referralCode) => {
    try {
      const body = { name, email: (email || "").trim().toLowerCase(), password };
      if (referralCode) body.referral_code = referralCode;
      const { data } = await api.post("/auth/register", body);
      if (data?.access_token) setStoredToken(data.access_token);
      setUser(data.user);
      identify(data.user);
      track.registerCompleted(referralCode);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setStoredToken(null);
    resetIdentity();
    setUser(false);
  };

  /**
   * Finalise a social login. Called by SocialFinishPage once it has either
   *   (a) exchanged a Google session_id with the backend, or
   *   (b) read a JWT off the URL fragment after an Apple/MS/Yahoo callback.
   *
   * `userOrNull` is the user object the backend returned; if it's null we
   * fall back to /auth/me (cookies were set, no body in the response).
   */
  const loginSocial = async ({ accessToken, user: userOrNull, isNew = false } = {}) => {
    if (accessToken) setStoredToken(accessToken);
    if (userOrNull) {
      setUser(userOrNull);
      identify(userOrNull);
      track[isNew ? "registerCompleted" : "loginSuccess"]?.();
      return { ok: true, user: userOrNull };
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      identify(data);
      track.loginSuccess();
      return { ok: true, user: data };
    } catch (e) {
      setStoredToken(null);
      setUser(false);
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, loginVerify2FA, register, logout, loginSocial, refresh: checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

// Re-export the token helper so any non-context code (e.g. one-off scripts)
// can read it if absolutely needed. Use sparingly — the context is the
// source of truth.
export { getStoredToken } from "../lib/api";

// Initial bootstrap — when the page reloads, AuthProvider's first
// render will call checkSession() which triggers /auth/me with the
// Authorization header attached automatically by api.js if a token
// is in localStorage. No separate bootstrap is needed.
void getStoredToken;
